import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Alert,
    LinearProgress,
    Stack
} from "@mui/material";
import {
    Timeline,
    Search,
    Refresh,
    Visibility,
    History,
    Compare,
    Download,
    Warning,
    CheckCircle,
    Error,
    Info
} from "@mui/icons-material";
import { serializationService } from "../../services/serialization/serializationService";

const EntityTraceabilityDashboard = () => {
    const [selectedEntityType, setSelectedEntityType] = useState("Opportunity");
    const [selectedEntityId, setSelectedEntityId] = useState("");
    const [entityList, setEntityList] = useState([]);
    const [traceabilityData, setTraceabilityData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const entityTypes = [
        { value: "Opportunity", label: "Oportunidades" },
        { value: "User", label: "Usuarios" },
        { value: "Investment", label: "Inversiones" },
        { value: "Agent", label: "Agentes" },
        { value: "Document", label: "Documentos" },
        { value: "KYC", label: "Verificaciones KYC" },
        { value: "Notification", label: "Notificaciones" },
        { value: "Message", label: "Mensajes" },
        { value: "Transaction", label: "Transacciones" },
        { value: "Activity", label: "Actividades" }
    ];

    // Mover la función aquí antes del useEffect
    const loadEntityList = useCallback(async () => {
        try {
            setLoading(true);
            const entities = await serializationService.getEntitiesByType(selectedEntityType);
            setEntityList(entities);
        } catch (error) {
            setError("Error cargando entidades: " + error.message);
        } finally {
            setLoading(false);
        }
    }, [selectedEntityType]);

    useEffect(() => {
        loadEntityList();
    }, [loadEntityList]);

    const loadTraceabilityData = async () => {
        if (!selectedEntityId) return;

        try {
            setLoading(true);
            setError("");

            const traceData = await serializationService.getEntityTraceability(
                selectedEntityType,
                selectedEntityId
            );
            setTraceabilityData(traceData);
        } catch (error) {
            setError("Error cargando trazabilidad: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
        case "active": return "success";
        case "deprecated": return "warning";
        case "error": return "error";
        default: return "default";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
        case "active": return <CheckCircle />;
        case "deprecated": return <Warning />;
        case "error": return <Error />;
        default: return <Info />;
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "N/A";
        // Manejar tanto timestamps de Firestore como fechas normales
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString();
    };

    const exportTraceabilityReport = async () => {
        if (!traceabilityData) return;

        try {
            const report = await serializationService.exportTraceabilityReport(
                selectedEntityType,
                selectedEntityId
            );
            
            // Crear y descargar archivo
            const blob = new Blob([JSON.stringify(report, null, 2)], {
                type: "application/json"
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `traceability-${selectedEntityType}-${selectedEntityId}.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            setError("Error exportando reporte: " + error.message);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
                {/* Header */}
                <Box>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
                        Dashboard de Trazabilidad por Entidad
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Visualiza y analiza la trazabilidad completa de entidades en el sistema de serialización
                    </Typography>
                </Box>

                {/* Filtros */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Filtros de Búsqueda
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Tipo de Entidad</InputLabel>
                                    <Select
                                        value={selectedEntityType}
                                        onChange={(e) => setSelectedEntityType(e.target.value)}
                                        label="Tipo de Entidad"
                                    >
                                        {entityTypes.map((type) => (
                                            <MenuItem key={type.value} value={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Entidad Específica</InputLabel>
                                    <Select
                                        value={selectedEntityId}
                                        onChange={(e) => setSelectedEntityId(e.target.value)}
                                        label="Entidad Específica"
                                        disabled={entityList.length === 0}
                                    >
                                        {entityList.map((entity) => (
                                            <MenuItem key={entity.id} value={entity.id}>
                                                {entity.name || entity.id}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="contained"
                                        startIcon={<Search />}
                                        onClick={loadTraceabilityData}
                                        disabled={!selectedEntityId || loading}
                                        fullWidth
                                    >
                                        Buscar Trazabilidad
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Refresh />}
                                        onClick={loadEntityList}
                                        disabled={loading}
                                    >
                                        <Tooltip title="Actualizar lista">
                                            <Refresh />
                                        </Tooltip>
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                {/* Loading */}
                {loading && <LinearProgress />}

                {/* Trazabilidad Data */}
                {traceabilityData && (
                    <Stack spacing={3}>
                        {/* Resumen */}
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Resumen de Trazabilidad
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={6} md={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" color="primary">
                                                {traceabilityData.totalVersions}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Versiones Totales
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" color="success.main">
                                                {traceabilityData.activeVersions}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Versiones Activas
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" color="warning.main">
                                                {traceabilityData.deprecatedVersions}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Versiones Deprecadas
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box textAlign="center">
                                            <Typography variant="h4" color="error.main">
                                                {traceabilityData.errorVersions}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Versiones con Errores
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Historial de Versiones */}
                        <Card>
                            <CardContent>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography variant="h6">
                                        Historial de Versiones
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Download />}
                                        onClick={exportTraceabilityReport}
                                    >
                                        Exportar Reporte
                                    </Button>
                                </Box>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Versión</TableCell>
                                                <TableCell>Estado</TableCell>
                                                <TableCell>Serializado Por</TableCell>
                                                <TableCell>Fecha</TableCell>
                                                <TableCell>Razón del Cambio</TableCell>
                                                <TableCell>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {traceabilityData.versions.map((version, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Chip 
                                                            label={version.version} 
                                                            size="small" 
                                                            color="primary"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            icon={getStatusIcon(version.status)}
                                                            label={version.status}
                                                            color={getStatusColor(version.status)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>{version.serializedBy}</TableCell>
                                                    <TableCell>
                                                        {formatTimestamp(version.serializedAt)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" noWrap>
                                                            {version.changeReason || "N/A"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={1}>
                                                            <Tooltip title="Ver Detalles">
                                                                <IconButton size="small">
                                                                    <Visibility />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Comparar">
                                                                <IconButton size="small">
                                                                    <Compare />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Historial">
                                                                <IconButton size="small">
                                                                    <History />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>

                        {/* Timeline de Cambios */}
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Timeline de Cambios
                                </Typography>
                                <Box sx={{ position: "relative", pl: 3 }}>
                                    {traceabilityData.versions.map((version, index) => (
                                        <Box key={index} sx={{ mb: 2 }}>
                                            <Box sx={{ 
                                                position: "absolute", 
                                                left: -8, 
                                                top: 8,
                                                width: 16, 
                                                height: 16, 
                                                borderRadius: "50%",
                                                backgroundColor: getStatusColor(version.status) === "success" ? "success.main" : 
                                                    getStatusColor(version.status) === "warning" ? "warning.main" : 
                                                        getStatusColor(version.status) === "error" ? "error.main" : "grey.500"
                                            }} />
                                            {index < traceabilityData.versions.length - 1 && (
                                                <Box sx={{ 
                                                    position: "absolute", 
                                                    left: -1, 
                                                    top: 24,
                                                    width: 2, 
                                                    height: 40, 
                                                    backgroundColor: "grey.300"
                                                }} />
                                            )}
                                            <Box sx={{ ml: 2 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                                                    Versión {version.version}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatTimestamp(version.serializedAt)} - {version.serializedBy}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    {version.changeReason || "Sin razón especificada"}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    </Stack>
                )}

                {/* Estado Vacío */}
                {!traceabilityData && !loading && (
                    <Card>
                        <CardContent sx={{ textAlign: "center", py: 4 }}>
                            <Timeline sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Selecciona una entidad para ver su trazabilidad
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Usa los filtros de arriba para buscar una entidad específica y visualizar su historial completo de cambios
                            </Typography>
                        </CardContent>
                    </Card>
                )}
            </Stack>
        </Box>
    );
};

export default EntityTraceabilityDashboard; 